import React, { HTMLAttributes, PropsWithChildren } from 'react';
import cls from 'classnames';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import styles from './SelectableCard.module.scss';
import { toNonNullContext } from '../../utils/context';

export const SelectableCardContext = React.createContext<SelectableCardProps<unknown> | undefined>(
  undefined,
);

export const useSelectableCardContext = toNonNullContext(SelectableCardContext);

/**
 * A base card that displays a selection outline.
 * @param props
 * @returns
 */
function SelectableCard<T>(props: PropsWithChildren<SelectableCardProps<T>>) {
  const {
    isSelected = false,
    isDisabled = false,
    // we don't want to spread the `card` property on the element
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    card: _card,
    children,
    className,
    ...rest
  } = props;

  return (
    <SelectableCardContext.Provider value={props}>
      <article
        className={cls(styles.card, className, {
          [styles.selected]: isSelected,
          [styles.disabled]: isDisabled,
        })}
        {...rest}
      >
        {children}
      </article>
    </SelectableCardContext.Provider>
  );
}

function SelectableCardProgress<T>(props: {
  selector: (card: SelectableCardContext<T>) => number;
}) {
  const { card } = useSelectableCardContext();
  // We're trusting the caller to make sure that this value is
  // only rendered within the context of the correct generic
  const progress = props.selector(card as SelectableCardContext<T>);

  const percentage = Math.floor(progress * 100);
  return (
    <progress
      className={styles.progressBar}
      value={percentage}
      max={100}
      aria-valuenow={progress * 100}
      aria-valuemin={0}
      aria-valuemax={100}
    />
  );
}

function SelectableCardSubtitle() {
  const { card } = useSelectableCardContext();

  return <p className="m-0 text-60 body-small">{card.subtitle}</p>;
}

function SelectableCardTitle() {
  const { card } = useSelectableCardContext();
  return <h1 className="m-0 text-100 body-large">{card.title}</h1>;
}

function SelectableCardBadge({ outline }: { outline?: boolean }) {
  const { card } = useSelectableCardContext();
  return <div className={cls(outline && styles.badge)}>{card.badge}</div>;
}

function SelectableCardImage({
  children,
  alt,
  src,
}: PropsWithChildren<{ alt: string; src?: string }>) {
  const { card } = useSelectableCardContext();
  return (
    <div className={styles.imageWrapper}>
      {typeof src === 'string' ? (
        <LazyLoadImage className={styles.image} src={src} alt={alt} effect="opacity" />
      ) : typeof card.image === 'string' ? (
        <LazyLoadImage className={styles.image} src={card.image} alt={alt} effect="opacity" />
      ) : (
        card.image
      )}
      {children}
    </div>
  );
}

function SelectableCardCornerImage(props: { alt?: string }) {
  const { card } = useSelectableCardContext();

  if (!card.cornerImage) {
    return null;
  }

  return (
    <LazyLoadImage
      className={styles.pairedBreaker}
      src={card.cornerImage}
      alt={props.alt}
      effect="opacity"
    />
  );
}

function SelectableCardBody({ children }: PropsWithChildren<{}>) {
  return <div className={styles.cardBody}>{children}</div>;
}

/**
 * Builders for creating different kinds of cards depending on the variant needed
 */
SelectableCard.Title = SelectableCardTitle;
SelectableCard.Subtitle = SelectableCardSubtitle;
SelectableCard.Image = SelectableCardImage;
SelectableCard.CornerImage = SelectableCardCornerImage;
SelectableCard.Progress = SelectableCardProgress;
SelectableCard.Badge = SelectableCardBadge;
SelectableCard.Body = SelectableCardBody;

export type SelectableCardContext<T> = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  image?: string | React.ReactElement;
  // fully rounded component
  badge?: React.ReactNode | string;
  cornerImage?: string;
  // reserved for any extra data that might
  // need to be passed to the card
  ctx: T;
};

export type SelectableCardProps<T> = HTMLAttributes<HTMLDivElement> & {
  card: SelectableCardContext<T>;
  isSelected?: boolean;
  isDisabled?: boolean;
};

export default SelectableCard;
